import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useCookies } from 'react-cookie';
import { useRouter } from 'next/router';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { MemberEntity, GetMvpds } from 'model/api/GetMvpds';
import { useFlowManager } from 'lib/hooks';
import { setConfigToProfile, tokenValid } from 'lib/utils/helper';
import { MVPDLoginResponse, Profile } from 'lib/types/profileType';
import { profileConfig } from 'lib/utils/configs';
import { refreshToken } from 'lib/utils';
import { AppContext } from 'context/GlobalContextProvider';
import { setFlowStage } from 'context/state/reducer';
import { Messages, Loader, SEO } from 'components/common';
import ProviderSearchList from './components/ProviderSearchList';
import TopProviders from './components/TopProviders';
import { fadein } from 'styles/animations';

import {
  ACCOUNT_COOKIE_NAME_FOX,
  COOKIE_CODE_INFO,
  FLOW_STAGE_MVPD_AUTH,
  FLOW_STAGE_MVPD_SELECT,
  ROUTE_HOME,
  ROUTE_SUCCESS,
} from 'constants/index';

export type Props = {
  profile: Profile;
};

const MVPDPage = ({ profile }: Props): JSX.Element => {
  const {
    dispatch,
    state: { account },
  } = useContext(AppContext);
  const [cookies] = useCookies([ACCOUNT_COOKIE_NAME_FOX, COOKIE_CODE_INFO]);
  const router = useRouter();
  const [mvpdList, setMvpdList] = useState<MemberEntity[] | undefined>([] as MemberEntity[]);
  const topProviders: MemberEntity[] | undefined = mvpdList?.slice(0, 8);
  const { isNoCodeOrAccount, isNation, isAnonymous } = useFlowManager();

  useEffect(() => {
    router?.prefetch(ROUTE_HOME);
  }, [router]);

  useEffect(() => {
    if (isNoCodeOrAccount) {
      router?.push(ROUTE_HOME);
    }
  }, [cookies, isNoCodeOrAccount, router]);

  useEffect(() => {
    if (!profile?.getMvpds) return;

    const fetchMvpds = async (val: number) => {
      await refreshToken(profile, account);
      try {
        const response: GetMvpds = await profile?.getMvpds?.({ itemsPerPage: val });
        const { member } = response;
        setMvpdList(member);
      } catch (error) {
        return error;
      }
    };
    fetchMvpds(999);
    dispatch(setFlowStage(FLOW_STAGE_MVPD_SELECT));

    return () => {
      setMvpdList([] as MemberEntity[]);
    };
  }, [dispatch, profile, account]);

  const handleMVPDSelect = (adobePassId: string): void => {
    let updatedProfile;

    const getMvpdLink = async (adobePassId: string, regCode: string) => {
      if (!adobePassId) return;
      const newAuthData = await refreshToken(profile, account);
      const newAccessToken = newAuthData?.accessToken;

      try {
        updatedProfile = (await setConfigToProfile(profile, {
          appName: profileConfig.appName,
          appVersion: profileConfig.appVersion,
          accessToken:
            isAnonymous && newAccessToken
              ? newAccessToken
              : cookies[ACCOUNT_COOKIE_NAME_FOX]?.accessToken,
        })) as Profile;

        const getTokenTTL = jwtDecode<JwtPayload>(cookies[ACCOUNT_COOKIE_NAME_FOX]?.accessToken);

        const redirectUrl = `${window.location.origin}${ROUTE_SUCCESS}`;

        if (tokenValid(getTokenTTL)) {
          const response = await updatedProfile?.mvpdLogin?.({
            regCode,
            mvpdId: adobePassId,
            redirectUrl,
            ...(!isNation && { additionalHeaders: { 'X-Delegated-Auth-Flow-Off': true } }),
          });
          dispatch(setFlowStage(FLOW_STAGE_MVPD_AUTH));
          const { authenticateUrl } = response as MVPDLoginResponse;
          await router.push(`${authenticateUrl}`);
        }
      } catch (error) {
        await router.push(ROUTE_HOME);
      }
    };

    getMvpdLink(adobePassId, cookies[COOKIE_CODE_INFO]?.code);
  };

  if (isNoCodeOrAccount || !mvpdList || !topProviders) return <Loader abCenter />;

  return (
    <>
      <SEO title="MVPD" />
      <MVPD>
        <Messages />
        <TopProviders topProviders={topProviders} handleMVPDSelect={handleMVPDSelect} />
        <ProviderSearchList mvpdList={mvpdList} handleMVPDSelect={handleMVPDSelect} />
      </MVPD>
    </>
  ) as JSX.Element;
};

export default MVPDPage;

const MVPD = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  ${fadein};
`;
